import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form1098QDueDates = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Form 1098-Q deadlines for Tax Year 2025 | TaxZerone"
          description="Know the IRS Form 1098-Q deadlines for Tax Year 2025. Report qualified education contract information on time and stay compliant with help from TaxZerone."
          keywords=""
          canonical="resources/information-returns/form-1098-q-due-dates/"
          ogType="article"
          jsonLdPricing="2.49"
          jsonLdAggregateRatingValue="5.00"
          jsonLdAggregateRatingCount="57448"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 3,
            mt: 5,
            textAlign: 'center',
          }}
        >
          <span className="highlight-container">
            <span className="highlight">Form 1098-Q</span>
          </span>{' '}
          Due Dates
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          File on time, Stay Compliant!
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1098Qduedates`}
          >
            E-file Now!
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/information-returns/form-1098-q-due-dates/"
                  />

                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-07-07T09:00:00-05:00"
                      >
                        July 7, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-07-07T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        July 7, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                          Alexia Zepeda
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 1098-Q due dates at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="1" />
                      <span itemProp="name">
                        {'Annuity providers and insurance companies must '}
                        <Link
                          href="/information-returns/efile-irs-form-1098-q/"
                          sx={{ color: '#0202ff' }}
                        >
                          file Form 1098-Q
                        </Link>
                        {' and provide copies to participants.'}
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="2" />
                      <span itemProp="name">
                        {'Paper and electronic Form 1098-Q filings are '}
                        generally due
                        {' by '}
                        <strong style={{ fontWeight: 600 }}>February 28</strong>
                        {', and participant copies must be '}
                        furnished
                        {' by '}
                        <strong style={{ fontWeight: 600 }}>January 31</strong>.
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="3" />
                      <span itemProp="name">
                        {'Late, incorrect, or missing '}
                        <Link
                          href="/resources/information-returns/form-1098-q-instructions/"
                          sx={{ color: '#0202ff' }}
                        >
                          Form 1098-Q
                        </Link>
                        {
                          ' filings may result in IRS penalties based on the level of noncompliance.'
                        }
                      </span>
                    </li>
                  </Box>

                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>
                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If you’re responsible for reporting distributions from a
                  Qualified Tuition Program (QTP), also known as a 529 Plan, you
                  must{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1098-q/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    file IRS Form 1098-Q
                  </Link>
                  . This form helps the IRS track educational distributions and
                  ensures transparency for both account holders and
                  beneficiaries. Filing on time is essential to avoid penalties
                  and remain in compliance with IRS rules.{' '}
                </Typography>
                <Box
                  sx={{
                    pt: 2,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <b>Table of Contents </b>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#whatis" sx={{ color: '#0202ff' }}>
                          What is IRS Form 1098-Q?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#filing" sx={{ color: '#0202ff' }}>
                          Filing Deadlines of Form 1098-Q
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#penalties" sx={{ color: '#0202ff' }}>
                          Penalties
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="whatis"
                >
                  What is IRS Form 1098-Q?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {' '}
                  IRS Form 1098-Q is an information return used to report
                  details about
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    Qualifying Longevity Annuity Contracts (QLACs)
                  </span>{' '}
                  purchased from qualified retirement accounts, such as IRAs and
                  401(k) plans.
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  This form is filed by annuity providers or insurance companies
                  and is provided to the contract holder (participant) to help
                  ensure compliance with{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    retirement distribution rules
                  </span>
                  . The form tracks QLAC purchases and verifies that the
                  investment amount is correctly excluded from Required Minimum
                  Distributions (<span style={{ fontWeight: 600 }}>RMDs</span>)
                  until annuity payments begin, typically at age 85.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="filing"
                >
                  Filing Deadlines of Form 1098-Q
                </Typography>

                <Typography variant="body1" component="div" sx={{ mb: 2 }}>
                  The IRS has set clear deadlines for{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1098-q/ "
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    filing Form 1098-Q
                  </Link>{' '}
                  and providing a copy to the participant. Annuity providers and
                  insurance companies must adhere to these deadlines to ensure
                  compliance and avoid potential penalties
                </Typography>
                <Box component="div" sx={{ mb: 4 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              borderRight: '1px solid #828282',
                              fontWeight: 600,
                            }}
                          >
                            Filing type
                          </TableCell>

                          <TableCell
                            sx={{ color: '#444', width: 300, fontWeight: 600 }}
                          >
                            Deadline Date
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              fontWeight: 600,
                            }}
                          >
                            Paper filing
                          </TableCell>

                          <TableCell sx={{}}>March 2, 2026 </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Electronic filing
                          </TableCell>

                          <TableCell sx={{}}>March 2, 2026 </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Sending Participant Copies
                          </TableCell>

                          <TableCell sx={{}}>February 2, 2026 </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="penalties"
                >
                  Penalties
                </Typography>
                <Box component="div" sx={{ mb: 3 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              borderRight: '1px solid #828282',
                              fontWeight: 600,
                            }}
                          >
                            Days Late
                          </TableCell>
                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              borderRight: '1px solid #828282',
                              fontWeight: 600,
                            }}
                          >
                            Penalty per Form
                          </TableCell>
                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              borderRight: '1px solid #828282',
                              fontWeight: 600,
                            }}
                          >
                            Maximum Penalty per Year
                          </TableCell>

                          <TableCell
                            sx={{ color: '#444', width: 300, fontWeight: 600 }}
                          >
                            Maximum Penalty for Small Businesses
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              fontWeight: 600,
                            }}
                          >
                            Filed within 30 days after due date
                          </TableCell>
                          <TableCell sx={{ borderRight: '1px solid #828282' }}>
                            $60
                          </TableCell>
                          <TableCell sx={{ borderRight: '1px solid #828282' }}>
                            $683,000
                          </TableCell>
                          <TableCell sx={{}}>$239,000 </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Filed more than 30 days late but by August 1
                          </TableCell>
                          <TableCell sx={{ borderRight: '1px solid #828282' }}>
                            $130
                          </TableCell>
                          <TableCell sx={{ borderRight: '1px solid #828282' }}>
                            $2,049,000
                          </TableCell>
                          <TableCell sx={{}}>$683,000 </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Filed after August 1 or do not file
                          </TableCell>
                          <TableCell sx={{ borderRight: '1px solid #828282' }}>
                            $340
                          </TableCell>
                          <TableCell sx={{ borderRight: '1px solid #828282' }}>
                            $4,098,500
                          </TableCell>
                          <TableCell sx={{}}>$1,366,000 </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              borderRight: '1px solid #828282',
                            }}
                          >
                            International Disregard
                          </TableCell>
                          <TableCell sx={{ borderRight: '1px solid #828282' }}>
                            $680
                          </TableCell>
                          <TableCell sx={{ borderRight: '1px solid #828282' }}>
                            No maximum limit
                          </TableCell>
                          <TableCell sx={{}}>No maximum limit </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
                  <Accordion
                    defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      id=""
                      sx={{
                        px: 0,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Sources and References
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        The information given above is based on the IRS
                        guidelines. For more details, please refer to the{' '}
                        <Link
                          href="https://www.irs.gov/pub/irs-pdf/i1098q.pdf"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          {' '}
                          instructions of Form 1098-Q
                        </Link>{' '}
                        and{' '}
                        <Link
                          href="https://www.irs.gov/instructions/i1099gi"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          General instructions of Information returns
                        </Link>
                        .
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 2,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              File your Form 1098-Q with TaxZerone
            </Typography>
            <Typography
              variant="h3"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 2,
                lineHeight: 1.4,
              }}
            >
              Don&apos;t miss the deadline! TaxZerone ensures your submissions
              <br />
              on time and helps to avoid late penalties.
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1098Qduedates`}
              >
                E-file Now
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default Form1098QDueDates;
